// src/components/ExportActivitiesTable/index.js
import React, { useState } from "react";
import {
  IconButton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";

const baseHeaderConfig = [
  { id: "UserName", label: "Time Keeper", sortable: true },
  { id: "TimeSpent", label: "Time Spent", sortable: true },
  { id: "TransactionDate", label: "Transaction Date", sortable: true },
  { id: "StartTime", label: "Start Time", sortable: true },
  { id: "FinishTime", label: "Finish Time", sortable: true },
  { id: "ServiceOrActivity", label: "Service / Activity", sortable: true },
  { id: "CaseName", label: "Case Name", sortable: true },
  { id: "CaseNumber", label: "Case Number", sortable: true },
  { id: "CreatedAt", label: "Created", sortable: true },
];

const ExportActivitiesTable = ({ data = [], onEditClick }) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const userDetails = useSelector((state) => state.userReducer.userDetails);

  const showEditIcon = userDetails?.permission
    ?.map((prm) => prm?.permission_id)
    .includes(29);

  // ✅ Create header dynamically based on permission
  const headerConfig = showEditIcon
    ? [{ id: "Action", label: "Action", sortable: false }, ...baseHeaderConfig]
    : baseHeaderConfig;

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const isDateField = (field) =>
    ["ReceivedDate", "AdjournedDate", "CreatedAt", "UpdatedAt"].includes(field);

  const sortedData = data.slice().sort((a, b) => {
    if (!orderBy) return 0;

    const valueA = a[orderBy] || "";
    const valueB = b[orderBy] || "";

    if (isDateField(orderBy)) {
      const dateA = valueA ? new Date(valueA) : null;
      const dateB = valueB ? new Date(valueB) : null;

      if (!dateA && !dateB) return 0;
      if (!dateA) return order === "asc" ? 1 : -1;
      if (!dateB) return order === "asc" ? -1 : 1;

      return order === "asc" ? dateA - dateB : dateB - dateA;
    } else {
      return order === "asc"
        ? valueA.toString().localeCompare(valueB.toString())
        : valueB.toString().localeCompare(valueA.toString());
    }
  });

  return (
    <TableContainer>
      <TableHead>
        <TableRow>
          {headerConfig.map((header) => (
            <TableCell key={header.id}>
              {header.sortable ? (
                <TableSortLabel
                  active={orderBy === header.id}
                  direction={orderBy === header.id ? order : "asc"}
                  onClick={() => handleRequestSort(header.id)}
                >
                  {header.label}
                </TableSortLabel>
              ) : (
                header.label
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedData?.map((item, index) => (
          <TableRow key={index}>
            {showEditIcon && (
              <TableCell>
                <IconButton onClick={() => onEditClick(item)}>
                  <EditIcon />
                </IconButton>
              </TableCell>
            )}
            <TableCell>{item.UserName}</TableCell>
            <TableCell>{item.TimeSpent}</TableCell>
            <TableCell>
              {item.TransactionDate
                ? moment(item.TransactionDate).format("LL")
                : "null"}
            </TableCell>
            <TableCell>
              {item?.StartTime ? moment(item?.StartTime).format("LLL") : "null"}
            </TableCell>
            <TableCell>
              {item?.FinishTime
                ? moment(item?.FinishTime).format("LLL")
                : "null"}
            </TableCell>
            <TableCell>{item.ServiceOrActivity}</TableCell>
            <TableCell>{item.CaseName}</TableCell>
            <TableCell>{item.CaseNumber}</TableCell>
            <TableCell>
              {item.CreatedAt
                ? moment.utc(item.CreatedAt).local().format("LLL")
                : "null"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TableContainer>
  );
};

export default ExportActivitiesTable;
